/* ──────────────────────────────────────────────────────────────────────
 *  Pulse Connectors — Core Type Contracts
 *  All teams consume these types. Team 0 owns changes.
 * ────────────────────────────────────────────────────────────────────── */

// ─── Setup Session ───────────────────────────────────────────────────

export type SetupStatus =
  | "draft"
  | "auth_pending"
  | "configuring"
  | "reviewing"
  | "checkout_pending"
  | "payment_confirmed"
  | "provisioning"
  | "ready"
  | "failed";

export type ProvisioningStatus =
  | "checkout_pending"
  | "payment_confirmed"
  | "provisioning_started"
  | "assistant_created"
  | "mcp_enabled"
  | "ready"
  | "requires_support"
  | "failed";

export interface SetupSession {
  id: string;
  userId: string | null;
  status: SetupStatus;
  agent: CommercialAgentCatalogItem | null;
  template: AssistantTemplate | null;
  connections: ConnectionSelection[];
  rules: AssistantRules | null;
  plan: PlanId;
  stripeCheckoutSessionId: string | null;
  stripePaymentStatus: "pending" | "confirmed" | "failed" | null;
  provisioningStatus: ProvisioningStatus | null;
  source: string | null;
  industry: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Assistant Template ──────────────────────────────────────────────

export interface AssistantTemplate {
  id: string;
  name: string;
  description: string;
  bestFor: string;
  defaultConnections: string[];
  icon: string; // lucide icon name
  status: "available" | "custom_setup";
  sources: string;
  prepares: string;
  humanReview: string;
}

// ─── Commercial Agent Catalog ────────────────────────────────────────

export interface CommercialAgentCatalogItem {
  catalogId: string;
  personaId: string;
  category: string;
  name: string;
  summary: string;
  bestFor: string;
  sampleRequest: string;
  defaultConnectionIds: string[];
  minimumPlan: PlanId;
  setupStatus: "available" | "setup_required" | "custom_setup";
  eligibleForSelectedPlan: boolean;
  sortOrder: number;
}

// ─── Connection ──────────────────────────────────────────────────────

export type ConnectionStatus =
  | "available"
  | "setup_required"
  | "planned"
  | "custom_setup";

export type AccessMode =
  | "read_selected"
  | "draft_only"
  | "manual_upload"
  | "custom_review";

export interface ConnectionDefinition {
  id: string;
  name: string;
  purpose: string;
  status: ConnectionStatus;
  accessMode: AccessMode;
  riskNote: string | null;
  icon: string; // lucide icon name
}

export interface ConnectionSelection {
  connectionId: string;
  name: string;
  purpose: string;
  status: ConnectionStatus;
  accessMode: AccessMode;
  riskNote: string | null;
  selected: boolean;
}

// ─── Assistant Rules ─────────────────────────────────────────────────

export interface AssistantRules {
  name: string;
  role: string;
  mainJob: string;
  allowedSources: string[];
  blockedSources: string[];
  sensitiveDataAcknowledged: boolean;
  reviewOwner: string;
  outputDestination: string;
  tone: string;
  sampleRequest: string | null;
}

// ─── Plans ───────────────────────────────────────────────────────────

export type PlanId = "starter" | "team" | "business";

export interface PlanDefinition {
  id: PlanId;
  name: string;
  monthlyPrice: number | null;
  assistantLimit: number;
  connectionLevel: string;
  requestAllowance: string;
  supportLevel: string;
  bestFor: string;
  features: string[];
}

// ─── Provisioning ────────────────────────────────────────────────────

export interface ProvisioningRequest {
  setupSessionId: string;
  userId: string;
  email: string;
  plan: PlanId;
  agent: CommercialAgentCatalogItem;
  assistantTemplate: string;
  rules: AssistantRules;
  connections: ConnectionSelection[];
  source: string | null;
  industry: string | null;
}

export interface ProvisioningResponse {
  success: boolean;
  assistantId: string | null;
  entitlementId: string | null;
  provisioningStatus: ProvisioningStatus;
  error: string | null;
}

// ─── Entitlement ─────────────────────────────────────────────────────

export interface Entitlement {
  id: string;
  userId: string;
  plan: PlanId;
  assistantLimit: number;
  connectionsIncluded: string;
  status: "active" | "cancelled" | "past_due" | "trialing";
  stripeSubscriptionId: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
}

// ─── MCP Probe ───────────────────────────────────────────────────────

export type ProbeStatus =
  | "ready"
  | "needs_sign_in"
  | "payment_pending"
  | "setup_running"
  | "needs_support"
  | "unavailable";

export interface McpProbeResult {
  accountStatus: ProbeStatus;
  paymentActive: boolean;
  mcpEnabled: boolean;
  assistantExists: boolean;
  scopesAssigned: boolean;
  endpointHealthy: boolean;
  overallStatus: ProbeStatus;
}

// ─── API Error ───────────────────────────────────────────────────────

export type ErrorCode =
  | "AUTH_EXPIRED"
  | "SETUP_INCOMPLETE"
  | "CHECKOUT_CANCELLED"
  | "CHECKOUT_PENDING"
  | "CONNECTION_UNAVAILABLE"
  | "PLAN_LIMIT_REACHED"
  | "PROVISION_FAILED"
  | "STRIPE_WEBHOOK_FAILED"
  | "AGENT_HUB_UNREACHABLE"
  | "MCP_PROBE_FAILED"
  | "UNKNOWN_ERROR";

export interface ApiError {
  code: ErrorCode;
  message: string;
  recoveryAction: string;
  retryable: boolean;
}

// ─── Assistant (created) ─────────────────────────────────────────────

export interface UserAssistant {
  id: string;
  displayName: string;
  templateId: string;
  templateName: string;
  connections: ConnectionSelection[];
  rules: AssistantRules;
  plan: PlanId;
  provisioningStatus: ProvisioningStatus;
  mcpEnabled: boolean;
  lastUpdated: string;
  createdAt: string;
}

// ─── Setup Stepper ───────────────────────────────────────────────────

export type SetupStep =
  | "package"
  | "agent"
  | "connections"
  | "rules"
  | "review"
  | "checkout"
  | "activate";

export const SETUP_STEPS: SetupStep[] = [
  "package",
  "agent",
  "connections",
  "rules",
  "review",
  "checkout",
  "activate",
];

export const STEP_LABELS: Record<SetupStep, string> = {
  package: "Package",
  agent: "Agent",
  connections: "Connections",
  rules: "Rules",
  review: "Review",
  checkout: "Checkout",
  activate: "Activate",
};

export const STEP_ROUTES: Record<SetupStep, string> = {
  package: "/signup",
  agent: "/choose-assistant",
  connections: "/connections",
  rules: "/define",
  review: "/review",
  checkout: "/checkout/return",
  activate: "/connect",
};
